// Template-item helpers (Fulfillment Slice 2).
//
// Replaces the Slice 1 "JSON textarea" item-authoring path with a
// list-of-rows editor. Item shape stays minimal: `{ id, label }`.
// Response keys (e.g. tier_1_responses[id], tier_2_responses[id]) are
// keyed by item id, so renames are label-only — id stays stable to
// avoid orphaning live responses on a form that's already filled.
//
// All helpers are pure (no I/O, no globals) so the same code can drive
// both the templates-sub-tab editor and the fill/review modals'
// "compute id list from template" code paths without a network round
// trip.

export type IntakeTemplateItem = {
  id: string;
  label: string;
};

const ITEM_ID_PREFIX = "item-";
const MAX_SLUG_BODY_LENGTH = 60;

/**
 * Slugify a free-form label to a stable item id. Always prefixed with
 * `item-` so seed slugs (item-roof, item-floor, etc.) and editor-
 * authored ids share one namespace. Lowercase, alphanumerics + ASCII
 * dashes only; runs of non-alphanumerics collapse to a single dash.
 *
 * Empty / whitespace-only labels return `item-` (caller must dedupe
 * that against existing ids before persisting).
 */
export function slugifyToItemId(label: string): string {
  const body = label
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, MAX_SLUG_BODY_LENGTH);
  return `${ITEM_ID_PREFIX}${body}`;
}

/**
 * Pick a unique id for a new item built off `label`. If the slugified
 * id collides with an existing id in `items`, suffixes `-2`, `-3`, …
 * until one is free. Pure: deterministic given the same inputs.
 */
export function uniqueItemIdForLabel(
  label: string,
  items: readonly IntakeTemplateItem[]
): string {
  const base = slugifyToItemId(label);
  const existing = new Set(items.map((it) => it.id));
  if (!existing.has(base)) return base;
  let n = 2;
  while (existing.has(`${base}-${n}`)) n += 1;
  return `${base}-${n}`;
}

export function addItem(
  items: readonly IntakeTemplateItem[],
  label: string
): IntakeTemplateItem[] {
  const trimmed = label.trim();
  return [...items, { id: uniqueItemIdForLabel(trimmed, items), label: trimmed }];
}

export function removeItem(
  items: readonly IntakeTemplateItem[],
  id: string
): IntakeTemplateItem[] {
  return items.filter((it) => it.id !== id);
}

export function renameItemLabel(
  items: readonly IntakeTemplateItem[],
  id: string,
  newLabel: string
): IntakeTemplateItem[] {
  return items.map((it) => (it.id === id ? { ...it, label: newLabel } : it));
}

/**
 * Move the item at `idx` up by one position. Returns a new array;
 * out-of-bounds or top-of-list indices return a shallow copy
 * unchanged.
 */
export function moveItemUp(
  items: readonly IntakeTemplateItem[],
  idx: number
): IntakeTemplateItem[] {
  if (idx <= 0 || idx >= items.length) return items.slice();
  const next = items.slice();
  const [moved] = next.splice(idx, 1);
  next.splice(idx - 1, 0, moved);
  return next;
}

export function moveItemDown(
  items: readonly IntakeTemplateItem[],
  idx: number
): IntakeTemplateItem[] {
  if (idx < 0 || idx >= items.length - 1) return items.slice();
  const next = items.slice();
  const [moved] = next.splice(idx, 1);
  next.splice(idx + 1, 0, moved);
  return next;
}

/**
 * Coerce the opaque `unknown[]` shape persisted on
 * fulfillment_intake_templates.tier_*_items into the strict
 * `IntakeTemplateItem[]` shape this slice's UI expects. Drops rows
 * that don't carry both a non-empty string `id` and a non-empty
 * string `label`.
 *
 * Used at the boundary where the templates list arrives from the
 * server (where the column is jsonb, opaque) so the rest of the UI
 * can assume strict shape. Also used to detect "this template was
 * authored under Slice 1's free-form JSON path" — if coerce drops
 * every row, the matching modal falls back to JSON-textarea mode.
 */
export function coerceTemplateItems(raw: unknown): IntakeTemplateItem[] {
  if (!Array.isArray(raw)) return [];
  const out: IntakeTemplateItem[] = [];
  for (const r of raw) {
    if (!r || typeof r !== "object") continue;
    const o = r as Record<string, unknown>;
    if (typeof o.id !== "string" || o.id.length === 0) continue;
    if (typeof o.label !== "string" || o.label.length === 0) continue;
    out.push({ id: o.id, label: o.label });
  }
  return out;
}

export type TemplateItemsValidationError =
  | { ok: false; reason: string };

/**
 * Validate items right before sending to the upsert endpoint. Mirrors
 * the same checks the server should re-apply (defensive on both ends);
 * stable reason strings are tested against the unit-test file, so do
 * not break them without updating the test.
 */
export function validateTemplateItems(
  items: readonly IntakeTemplateItem[]
): { ok: true } | TemplateItemsValidationError {
  const seen = new Set<string>();
  for (let i = 0; i < items.length; i += 1) {
    const it = items[i];
    if (!it || typeof it !== "object") {
      return { ok: false, reason: `item at index ${i} is not an object` };
    }
    if (typeof it.id !== "string" || it.id.length === 0) {
      return { ok: false, reason: `item at index ${i} has an empty id` };
    }
    if (typeof it.label !== "string" || it.label.trim().length === 0) {
      return { ok: false, reason: `item "${it.id}" has an empty label` };
    }
    if (seen.has(it.id)) {
      return { ok: false, reason: `duplicate item id "${it.id}"` };
    }
    seen.add(it.id);
  }
  return { ok: true };
}
